// Shared constants and formatters for the superadmin panels. Extracted verbatim
// from the original single-file dashboard so panel behaviour is unchanged.
const TEMPLATE_META = {
  admin_reset_link: {
    label: "Admin Password Reset",
    description: "Sent when an admin resets a user's password.",
    variables: [
      "{{name}}",
      "{{company}}",
      "{{tempPassword}}",
      "{{setPasswordUrl}}",
      "{{expiresIn}}",
    ],
  },
  broadcast: {
    label: "Broadcast / Newsletter",
    description:
      "Wrapper template used for newsletters and release notes sent to tenant admins and their alert recipients.",
    variables: ["{{title}}", "{{content}}"],
  },
  company_invite: {
    label: "Company Admin Invite (set-password)",
    description:
      "Sent to a company admin who needs to set their password via a link.",
    variables: [
      "{{name}}",
      "{{company}}",
      "{{plan}}",
      "{{setPasswordUrl}}",
      "{{expiresIn}}",
    ],
  },
  company_welcome: {
    label: "Company Welcome",
    description:
      "Sent after a company admin completes self-registration on the sign-up page.",
    variables: ["{{name}}", "{{company}}", "{{plan}}", "{{loginUrl}}"],
  },
  forgot_password_link: {
    label: "Forgot Password",
    description: "Sent when a user requests a password reset link.",
    variables: ["{{name}}", "{{setPasswordUrl}}", "{{expiresIn}}"],
  },
  inventory_digest: {
    label: "Daily Inventory Report",
    description:
      "Sent nightly to the tenant admin with the inventory report workbook attached.",
    variables: [
      "{{date}}",
      "{{outOfStockCount}}",
      "{{lowStockCount}}",
      "{{movementCount}}",
      "{{stagnantCount}}",
      "{{partsTable}}",
    ],
  },
  otp: {
    label: "OTP / Verification Code",
    description: "Sent when a user requests a password reset code via OTP.",
    variables: ["{{otp}}"],
  },
  password_reset: {
    label: "Password Changed",
    description: "Sent after a user successfully changes their password.",
    variables: ["{{name}}"],
  },
  subscription_ending: {
    label: "Subscription Ending",
    description:
      "Sent to the tenant admin 3 days before the subscription expires and again on the expiry day.",
    variables: [
      "{{company}}",
      "{{plan}}",
      "{{daysLabel}}",
      "{{expiryDate}}",
      "{{manageUrl}}",
      "{{days}}",
    ],
  },
  user_invite: {
    label: "User Invite",
    description: "Sent when a new user is invited to a company workspace.",
    variables: [
      "{{name}}",
      "{{company}}",
      "{{tempPassword}}",
      "{{setPasswordUrl}}",
      "{{expiresIn}}",
    ],
  },
};

const DEFAULT_BASE_PRICE = 499;
const DEFAULT_DISCOUNT_BY_MONTH = {
  1: 0,
  3: 5,
  6: 10,
  12: 15,
};

const PAYMENT_STATUS_OPTIONS = [
  "PENDING",
  "TRIAL",
  "ACTIVE",
  "PAST_DUE",
  "FAILED",
  "REFUNDED",
  "CANCELLED",
];

const AUDIT_MODULE_LABELS = {
  USER_MANAGEMENT: "User Management",
  ROLE_MANAGEMENT: "Role Management",
  WORKFLOW_MANAGEMENT: "Workflow Management",
  PROJECT_MANAGEMENT: "Project Management",
  AUTH: "Authentication",
  TENANT_SETTINGS: "Tenant Settings",
  BILLING: "Billing & Payments",
  PLATFORM: "Platform",
};

const AUDIT_ACTION_COLORS = {
  CREATE: "#16a34a",
  UPDATE: "#0f62c7",
  DELETE: "#dc2626",
  ASSIGN: "#7c3aed",
  STATUS_CHANGE: "#b45309",
  PASSWORD_RESET: "#b45309",
  PASSWORD_CHANGE: "#b45309",
  LOGIN: "#0f766e",
  LOGOUT: "#64748b",
  LOGIN_FAILED: "#dc2626",
  PAYMENT: "#16a34a",
  PAYMENT_FAILED: "#dc2626",
  REFUND: "#b45309",
};

const EMPTY_AUDIT_FILTERS = {
  tenantId: "",
  module: "",
  action: "",
  entityType: "",
  status: "",
  search: "",
  from: "",
  to: "",
};

const AUDIT_PAGE_SIZE = 25;

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Audit values can be arrays (roles, permissions, stage lists) or objects, and
// the dashboard has to render both readably in a table cell.
function formatAuditValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "(none)";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN");
}

function formatInr(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Number(value) || 0));
}

export {
  TEMPLATE_META,
  DEFAULT_BASE_PRICE,
  DEFAULT_DISCOUNT_BY_MONTH,
  PAYMENT_STATUS_OPTIONS,
  AUDIT_MODULE_LABELS,
  AUDIT_ACTION_COLORS,
  EMPTY_AUDIT_FILTERS,
  AUDIT_PAGE_SIZE,
  formatDateTime,
  formatAuditValue,
  formatDate,
  formatInr,
};
